import { getNegociateurName } from './negociateur';

/**
 * propertyListHelpers.js
 * 
 * Helpers pour affichage liste biens
 * Mapping centralisé de la nouvelle structure vers affichage UI
 */

import { getPrixLabel, getPrixValue, getSurfaceValue, getSurfaceLabel, getVille, getSecteur, getTitreMetier, getActiviteMetier, getDroitEntree, getLoyerMensuel } from './propertyDataMapper';
import { formatPriceFR } from './formatFR';

/**
 * Obtenir le statut d'occupation du bien
 * @param {Object} property
 * @returns {string} - "Libre", "Loué", ou null
 */
function getStatutOccupation(property) {
  // Pour l'instant, retourne null si pas de données
  // TODO: implémenter logique selon bien_general.statut_bien ou informations_bail.locataire
  const operation = property.taxonomy?.operation || property.operation;
  
  // Pour les locations, on pourrait vérifier s'il y a un locataire
  if (operation && operation.includes('location')) {
    const hasLocataire = property.informations_bail?.locataire_nom;
    return hasLocataire ? 'Loué' : 'Libre';
  }
  
  // Pour les ventes/cessions : pas de statut d'occupation pertinent
  return null;
}

/**
 * Formater le montant en euros
 * @param {number|null} value - Montant
 * @returns {string} - Montant formaté ou "N/C"
 */
function formatMontant(value) {
  if (!value) return 'N/C';
  return formatPriceFR(value);
}

/**
 * Obtenir l'affichage prix spécifique pour location_droit_entree
 * Format : "Pas-de-porte : X € • Loyer : Y €/mois"
 * @param {Object} property
 * @returns {string}
 */
function getPrixLocationDroitEntree(property) {
  const droitEntree = getDroitEntree(property);
  const loyer = getLoyerMensuel(property);
  
  const droitEntreeStr = droitEntree ? formatMontant(droitEntree) : 'N/C';
  const loyerStr = loyer ? `${formatMontant(loyer)}/mois` : 'N/C';
  
  return `Pas-de-porte : ${droitEntreeStr} • Loyer : ${loyerStr}`;
}

/**
 * Obtenir les données d'affichage pour la liste
 * @param {Object} property - Bien avec nouvelle structure (taxonomy, public_location, etc.)
 * @returns {Object} - Données formatées pour affichage
 */
export function getPropertyListDisplayData(property) {
  if (!property) return {};
  
  const taxonomy = property.taxonomy || {};
  const composition = property.composition || {};
  const market = property.market || {};
  const deal = property.deal || {};
  const negociateur = property.negociateur || property.mandat?.negociateur || {};
  
  // Gestion spéciale du prix pour location_droit_entree
  const operation = taxonomy.operation || property.operation;
  let prixDisplay;
  if (operation === 'location_droit_entree') {
    prixDisplay = getPrixLocationDroitEntree(property);
  } else {
    prixDisplay = getPrixLabel(property);
  }
  
  // Infos spécifiques Services Libéraux
  const profil = property.profil || taxonomy.profil;
  const sous_profil = property.sous_profil || taxonomy.sous_profil;
  let servicesLiberauxInfo = null;
  
  if (profil === 'services_liberaux') {
    const infos = [];
    
    // Nombre de pièces
    if (composition.nb_pieces) {
      infos.push(`${composition.nb_pieces} pièce${composition.nb_pieces > 1 ? 's' : ''}`);
    }
    
    // Salles de consultation (médical)
    if (composition.nb_salles_consultation) {
      infos.push(`${composition.nb_salles_consultation} salle${composition.nb_salles_consultation > 1 ? 's' : ''} consult.`);
    }
    
    // Fauteuils dentaires
    if (composition.nb_fauteuils_dentaires) {
      infos.push(`${composition.nb_fauteuils_dentaires} fauteuil${composition.nb_fauteuils_dentaires > 1 ? 's' : ''}`);
    }
    
    // Salles de réunion (juridique)
    if (composition.nb_salles_reunion) {
      infos.push(`${composition.nb_salles_reunion} salle${composition.nb_salles_reunion > 1 ? 's' : ''} réunion`);
    }
    
    // Sanitaires
    if (composition.nb_sanitaires) {
      infos.push(`${composition.nb_sanitaires} WC`);
    }
    
    // Salle d'attente
    if (composition.salle_attente) {
      infos.push('Salle attente');
    }
    
    // PMR
    if (composition.pmr_obligatoire) {
      infos.push('PMR ✓');
    }
    
    servicesLiberauxInfo = infos.length > 0 ? infos.join(' • ') : null;
  }
  
  return {
    // Type bien → utiliser le mapper centralisé AVEC LABELS MÉTIER
    typeBien: getTitreMetier(property),
    activite: getActiviteMetier(property),
    operation: taxonomy.operation || property.operation || 'NC',
    profil: profil,
    sous_profil: sous_profil,
    
    // Localisation → utiliser le mapper centralisé
    ville: getVille(property),
    secteur: getSecteur(property),
    codeEmplacement: property.public_location?.emplacement_code || '',
    
    // Surface → utiliser le mapper centralisé
    surface: getSurfaceValue(property) || 0,
    surfaceLabel: getSurfaceLabel(property),
    
    // Prix/Loyer → utiliser le mapper centralisé (avec cas spécial location_droit_entree)
    prix: prixDisplay,
    prixRaw: getPrixValue(property) || 0,
    
    // Statuts
    statut: getStatutOccupation(property), // "Libre" ou "Loué"
    status: property.status || 'draft', // draft/active/archived
    marketStatus: market.status || 'off_market',
    dealStatus: deal.status || 'open',
    
    // Négociateur
    negociateur: getNegociateurName(property, [], 'NC'),
    
    // Dates
    createdAt: property.created_at,
    updatedAt: property.updated_at,
    
    // Médias
    photosCount: property.medias?.photos?.length || 0,
    photoUrl: property.medias?.photos?.[0]?.url || null,
    
    // IDs & Référence
    id: property.id,
    // lot AG3 — jamais l'UUID technique : il n'a aucun sens pour un négociateur (la vue liste
    // affiche déjà « — » dans ce cas).
    reference: property.reference || '—',
    
    // Services Libéraux (NEW)
    servicesLiberauxInfo: servicesLiberauxInfo
  };
}

/**
 * Obtenir l'icône selon le type de bien
 */
export function getPropertyIcon(taxonomy) {
  if (!taxonomy) return 'bi-building';
  
  const objet = taxonomy.objet?.toLowerCase() || '';
  const profil = taxonomy.profil?.toLowerCase() || '';
  
  if (objet.includes('fonds') || profil.includes('commerce')) return 'bi-shop';
  if (objet.includes('murs') || profil.includes('bureaux')) return 'bi-building';
  if (profil.includes('chr') || profil.includes('restaurant')) return 'bi-cup-hot';
  if (profil.includes('hôtel') || profil.includes('hotel')) return 'bi-hospital';
  if (profil.includes('entrepôt') || profil.includes('logistique')) return 'bi-boxes';
  
  return 'bi-building';
}

/**
 * Obtenir le badge de statut
 */
export function getStatusBadge(property) {
  if (!property || typeof property !== 'object') {
    return { label: 'Inconnu', class: 'bg-slate-100 text-slate-600 border-slate-300' };
  }
  const status = property.status || property.statut;
  const marketStatus = property.market?.status;
  const dealStatus = property.deal?.status;
  
  // Deal status prioritaire — badges "transaction conclue" : palette dédiée sombre
  // (distincte du vert "disponible" et du violet IA)
  if (dealStatus === 'closed_sale') {
    return { label: 'Vendu', class: 'bg-slate-900 text-white border-slate-900' };
  }
  if (dealStatus === 'closed_let') {
    return { label: 'Loué', class: 'bg-slate-700 text-white border-slate-700' };
  }
  if (dealStatus === 'cancelled') {
    return { label: 'Annulé', class: 'bg-gray-100 text-gray-700 border-gray-300' };
  }
  
  // Market status
  if (marketStatus === 'off_market') {
    return { label: 'Hors marché', class: 'bg-orange-100 text-orange-700 border-orange-300' };
  }
  
  // Property status
  if (status === 'sold' || status === 'vendu') {
    return { label: 'Vendu', class: 'bg-slate-900 text-white border-slate-900' };
  }
  if (status === 'archived') {
    return { label: 'Archivé', class: 'bg-gray-100 text-gray-600 border-gray-300' };
  }
  // lot BX — un bien supprimé s'affichait « Inconnu » (page Diffusion notamment).
  if (status === 'deleted') {
    return { label: 'Supprimé', class: 'bg-red-50 text-red-700 border-red-200' };
  }
  if (status === 'draft') {
    return { label: 'Brouillon', class: 'bg-yellow-100 text-yellow-700 border-yellow-300' };
  }
  if (status === 'active') {
    return { label: 'Actif', class: 'bg-green-100 text-green-700 border-green-300' };
  }
  
  return { label: 'Inconnu', class: 'bg-slate-100 text-slate-600 border-slate-300' };
}

export default {
  getPropertyListDisplayData,
  getPropertyIcon,
  getStatusBadge
};
